import {
  QueryOptionFilterValue,
  PropertyTypeWithoutOrder,
  IQueryOptionFilterOrderByDTO,
  IQueryOptionFilterWhereDTO,
  IQueryOptionsByFieldIdOrderBy,
  IQueryOptionsByFieldIdWhere,
  IMakeQueryOptionsDtoWhereReturn,
  MakeQueryOptionsDtoArgs,
  MakeQueryOptionsDtoReturn,
  PropertyType,
} from '@global/api/types/queryOptions'
import { Operator, valueRequiredOperand } from '@global/constants'
import { ColumnDataType } from '@global/types'

function isStringValue(value: any): value is string {
  return typeof value === 'string'
}

function isQueryOptionFilterValueWithKey(value: any, key: string): value is QueryOptionFilterValue {
  return typeof value === 'object' && value !== null && key in value
}

/**
 * Convert filter value to the correct type for the backend
 * based on the operator, fieldId, and value. Some fields require
 * special handling, such as the createdById, updatedById, and
 * submissionId fields.
 *
 * @param filter
 * @returns string | string[] | number | number[] | undefined
 *
 */
function getConvertedFilterValue(filter: any): any {
  const { value, fieldId, operator } = filter

  if (operator === Operator.EQUALS && fieldId === 'properties.createdById' && value?.id) {
    return `${value.id}`
  }

  if (
    Array.isArray(value) &&
    fieldId &&
    ['properties.createdById', 'properties.updatedById', 'properties.submissionId'].includes(
      fieldId,
    )
  ) {
    return value.flatMap(v => (typeof v.id === 'number' ? v.id : v.id?.split(',')))
  }

  if (
    fieldId === 'properties.submissionId' &&
    isQueryOptionFilterValueWithKey(value, 'id') &&
    isStringValue(value.id)
  ) {
    return value.id.split(',')
  }

  return isQueryOptionFilterValueWithKey(value, 'id') && isStringValue(value.id) ? value.id : value
}

function makeQueryOptionsDtoWhere(
  queryOptionsByFieldIdWhere: IQueryOptionsByFieldIdWhere,
): IMakeQueryOptionsDtoWhereReturn {
  const sanitizeFilters = filter => !valueRequiredOperand(filter?.operator) || !!filter?.value

  const filterExpressions = Object.entries(queryOptionsByFieldIdWhere).reduce<
    IQueryOptionFilterWhereDTO[]
  >((memo, [_, queryOptions]) => {
    const additionalFilters = queryOptions.filters
      .filter(sanitizeFilters)
      .filter(filter => filter.operator)
      .map(filter => {
        const { operator, fieldId, value } = filter

        const isFieldSpecified = fieldId !== null && fieldId !== 'ALL'
        const isLessThanOrGreaterThan =
          operator === Operator.LESS_THAN || operator === Operator.GREATER_THAN

        const isValueSpecified = value !== null && value !== ''

        const filterDto: IQueryOptionFilterWhereDTO = {
          operator,
          fieldId,
          column: isFieldSpecified ? fieldId : undefined,
          asString: isLessThanOrGreaterThan,
          value: isValueSpecified ? getConvertedFilterValue(filter) : undefined,
        }

        return filterDto
      })
    return memo.concat(additionalFilters)
  }, [])

  return {
    operator: '_and',
    value: filterExpressions,
  }
}

function makeQueryOptionsDtoOrderBy(queryOptionsByFieldIdOrderBy: IQueryOptionsByFieldIdOrderBy) {
  return Object.entries(queryOptionsByFieldIdOrderBy).reduce<IQueryOptionFilterOrderByDTO[]>(
    (memo, [fieldId, queryOptions]) => {
      if (queryOptions.sortOrder) {
        memo.push({
          column: fieldId as PropertyTypeWithoutOrder,
          order: queryOptions.sortOrder,
        })
      }
      return memo
    },
    [],
  )
}

export function makeQueryOptionsDto({
  optionsByFieldId,
  queryOffset,
  queryLimit,
  queryCommitRef,
  includeComments,
  includeRowProperties,
  classificationCategoryIds = [],
}: MakeQueryOptionsDtoArgs): MakeQueryOptionsDtoReturn {
  const where = optionsByFieldId
    ? makeQueryOptionsDtoWhere(optionsByFieldId)
    : {
        operator: '_and',
        value: [],
      }
  const orderBy = optionsByFieldId ? makeQueryOptionsDtoOrderBy(optionsByFieldId) : []
  const searchTerm = makeQueryOptionsDtoSearch(optionsByFieldId)

  // Search is added to the existing where filter as '_contains' so we avoid Backend refactor.
  if (searchTerm.length) {
    where.value.unshift(...searchTerm)
  }

  const queryOptionsDto: MakeQueryOptionsDtoReturn = {
    ...(queryCommitRef && { commitRef: queryCommitRef }),
    where,
    orderBy,
    offset: queryOffset ?? 0,
    limit: queryLimit ?? 0,
  }

  if (includeComments) {
    queryOptionsDto.includeComments = true
  }

  if (includeRowProperties) {
    queryOptionsDto.includeRowProperties = true
  }

  if (classificationCategoryIds.length) {
    queryOptionsDto.includeClassifications = true
    queryOptionsDto.classificationCategoryIds = classificationCategoryIds
  }

  return queryOptionsDto
}

function makeQueryOptionsDtoSearch(
  queryOptionsByFieldId: IQueryOptionsByFieldIdOrderBy & IQueryOptionsByFieldIdWhere,
): IQueryOptionFilterWhereDTO[] {
  return Object.entries(queryOptionsByFieldId).reduce<IQueryOptionFilterWhereDTO[]>(
    (memo, [fieldId, queryOptions]) => {
      if (queryOptions.searchTerm) {
        const queryOptionFilterWhereDTO: Pick<IQueryOptionFilterWhereDTO, 'column' | 'fieldId'> = {
          column: fieldId as PropertyTypeWithoutOrder,
          fieldId: fieldId as PropertyType,
        }
        // if columnDataType is submission we mimic "in" operator
        if (queryOptions.columnDataType === ColumnDataType.SUBMISSION) {
          memo.push({
            operator: Operator.IN,
            value: queryOptions.submissionIds,
            ...queryOptionFilterWhereDTO,
          })
        } else {
          memo.push({
            operator: Operator.CONTAINS,
            value: queryOptions.searchTerm,
            ...queryOptionFilterWhereDTO,
          })
        }
      }
      return memo
    },
    [],
  )
}
